import { useState } from "react";

import { Button, PasswordInput } from "@newsela/angelou";

import { OnResetPasswordReturn } from "../ResetAccountPassword";
import {
  fieldsContainerCss,
  passwordInputCss,
  submitButtonCss,
} from "./MyOwnPasswordReset.style";

export interface MyOwnPasswordResetProps {
  minPasswordLength: number;
  onResetPasswordSubmit: (
    payload: ResetPasswordPayload,
  ) => OnResetPasswordReturn;
  canEdit: boolean;
}

export interface ResetPasswordPayload {
  oldPassword: string | undefined;
  newPassword: string | undefined;
  confirmPassword: string | undefined;
}

const initialFormState: ResetPasswordPayload = {
  oldPassword: undefined,
  newPassword: undefined,
  confirmPassword: undefined,
};

const MyOwnPasswordReset = ({
  minPasswordLength,
  onResetPasswordSubmit,
  canEdit,
}: MyOwnPasswordResetProps) => {
  const [form, setForm] = useState<ResetPasswordPayload>(initialFormState);

  const hasNewPasswordMinLength = form.newPassword
    ? form.newPassword?.length >= minPasswordLength
    : false;
  const hasMatchedConfirmPassword = form.newPassword === form.confirmPassword;

  const handleResetPasswordClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    void onResetPasswordSubmit(form);
    setForm(initialFormState);
  };

  const isValidForm = () =>
    form.oldPassword && hasNewPasswordMinLength && hasMatchedConfirmPassword;

  return (
    <form role="form">
      <div css={fieldsContainerCss}>
        <PasswordInput
          name="current password"
          label="Current password"
          placeholder="Start typing..."
          __cssFor={{ root: passwordInputCss }}
          onChange={(e) =>
            setForm((form) => ({ ...form, oldPassword: e.target.value }))
          }
          value={form.oldPassword || ""}
          disabled={!canEdit}
          underInputId="password-input-old-id"
        />
        <PasswordInput
          name="new password"
          label="New password"
          placeholder="Start typing..."
          __cssFor={{ root: passwordInputCss }}
          onChange={(e) =>
            setForm((form) => ({ ...form, newPassword: e.target.value }))
          }
          value={form.newPassword || ""}
          disabled={!canEdit}
          touched={Boolean(form.newPassword)}
          error={
            hasNewPasswordMinLength
              ? ""
              : `Must be at least ${minPasswordLength} characters long`
          }
          underInputId="password-input-new-id"
        />
        <PasswordInput
          name="confirm new password"
          label="Confirm new password"
          placeholder="Start typing..."
          __cssFor={{ root: passwordInputCss }}
          onChange={(e) =>
            setForm((form) => ({ ...form, confirmPassword: e.target.value }))
          }
          value={form.confirmPassword || ""}
          disabled={!canEdit}
          touched={Boolean(form.confirmPassword)}
          error={hasMatchedConfirmPassword ? "" : "Passwords do not match"}
          underInputId="password-input-confirm-id"
        />
      </div>
      <Button
        flavor={Button.flavors.primary}
        onClick={handleResetPasswordClick}
        disabled={!canEdit || !isValidForm()}
        type="submit"
        __cssFor={{ root: submitButtonCss }}
      >
        Update password
      </Button>
    </form>
  );
};

export default MyOwnPasswordReset;
